import URI from 'urijs'
import Vue from 'vue'
import { config } from '@/plugins/env.config'
import { status, types, orderBy } from '@/config/authorization'
import { decamelizeKeys } from 'humps'

async function getAuthorizations (providerSlug, filters, attemptNumber = 1) {
  const getAuthorizationsUri = new URI(config.apiBaseUrl)
  getAuthorizationsUri.segment('gnp-arc')
  getAuthorizationsUri.segment('v1')
  getAuthorizationsUri.segment('providers')
  getAuthorizationsUri.segment(providerSlug)
  getAuthorizationsUri.segment('outpatient-care-claim')
  if (filters.createdFrom) {
    getAuthorizationsUri.addSearch('date_from', filters.createdFrom)
  }
  
  if (filters.createdTo) {
    getAuthorizationsUri.addSearch('date_to', filters.createdTo)
  }
  
  if (filters.statuses) {
    getAuthorizationsUri.addSearch('claim_status', filters.statuses)
  }

  if (filters.codeSearchValue) {
    getAuthorizationsUri.addSearch('claim_id', filters.codeSearchValue)
  }

  if (filters.orderBy) {
    getAuthorizationsUri.addSearch('sort', filters.orderBy)
  }
  getAuthorizationsUri.addSearch('page', filters.page)
  getAuthorizationsUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAuthorizationsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizations(providerSlug, filters, attemptNumber + 1)
    }

    throw error
  }
}

async function sendBiometricValidation(providerSlug, payload, attemptNumber = 1) {
  const biometricValidationUri = new URI(config.apiBaseUrl)
  biometricValidationUri.segment('gnp-arc')
  biometricValidationUri.segment('v1')
  biometricValidationUri.segment('providers')
  biometricValidationUri.segment(providerSlug)
  biometricValidationUri.segment('biometric-reading')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(biometricValidationUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendBiometricValidation(providerSlug, payload, attemptNumber + 1)
    }

    throw error
  }
}

async function startClaimAuthorization(providerSlug, payload, attemptNumber = 1) {
  const startClaimAuthorizationUri = new URI(config.apiBaseUrl)
  startClaimAuthorizationUri.segment('gnp-arc')
  startClaimAuthorizationUri.segment('v1')
  startClaimAuthorizationUri.segment('providers')
  startClaimAuthorizationUri.segment(providerSlug)
  startClaimAuthorizationUri.segment('outpatient-care-claim')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(startClaimAuthorizationUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return startClaimAuthorization(providerSlug, payload, attemptNumber + 1)
    }

    throw error
  }
}

async function getClaimProcessMonitor(payload, attemptNumber = 1) {
  const monitorId = new URI(payload)

  try {
    const response = await Vue.prototype.$http.get(monitorId)

    if (response.data.status === 'PENDING') {
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
      return getClaimProcessMonitor(payload)
    }

    return response
  } catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getClaimProcessMonitor(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function completeClaimAuthorization(providerSlug, claimId, payload, attemptNumber = 1) {
  const completeClaimUri = new URI(config.apiBaseUrl)
  completeClaimUri.segment('gnp-arc')
  completeClaimUri.segment('v1')
  completeClaimUri.segment('providers')
  completeClaimUri.segment(providerSlug)
  completeClaimUri.segment('outpatient-care-claim')
  completeClaimUri.segment(claimId.toString())
  completeClaimUri.segment('complete')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(completeClaimUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeClaimAuthorization(providerSlug, claimId, payload, attemptNumber + 1)
    }

    throw error
  }
}

function getStatus(authorization) {
  const statusByCoverage = status.find(configuration => configuration.coverageType === authorization.coverageType)
  if (statusByCoverage) {
    const configurationState = statusByCoverage.states.find(state => state.value === authorization.authorizationStatus);
    return configurationState && configurationState.text || '';
  }
  return getStatus({ coverageType: 'DEFAULT', authorizationStatus: authorization.authorizationStatus })
}

function getType(authorizationType) {
   const configurationType = types.find(type => type.slug === authorizationType);
  return configurationType && configurationType.text || '';
}

export {
  getAuthorizations,
  sendBiometricValidation,
  startClaimAuthorization,
  getClaimProcessMonitor,
  completeClaimAuthorization,
  getStatus,
  getType,
  status,
  orderBy
}
